/* Progressive enhancement: Shopify's native product form remains usable without JS. */
if (!customElements.get('v3-gallery')) {
  customElements.define('v3-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const track = this.querySelector('.v3-gallery__track');
      const buttons = [...this.querySelectorAll('[data-slide]')];
      buttons.forEach(button => button.addEventListener('click', () => {
        track.scrollTo({ left: Number(button.dataset.slide) * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }));
      track.addEventListener('scroll', () => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        buttons.forEach((button, i) => i === index ? button.setAttribute('aria-current', 'true') : button.removeAttribute('aria-current'));
      }, { passive: true });
    }
  });
}
if (!customElements.get('v3-purchase')) {
  customElements.define('v3-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const select = this.querySelector('select[name="id"]');
      if (!select) return;
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const button = this.querySelector('[name="add"]');
        const available = option.dataset.available === 'true';
        this.querySelector('[data-variant-price]').textContent = option.dataset.price;
        button.disabled = !available;
        button.textContent = available ? button.dataset.addLabel : button.dataset.soldLabel;
        const section = this.closest('.v3-pdp');
        const sticky = section?.querySelector('.v3-mobile-buy');
        if (sticky) {
          sticky.querySelector('[data-sticky-price]').textContent = option.dataset.price;
          const stickyButton = sticky.querySelector('button');
          stickyButton.disabled = !available;
          stickyButton.textContent = available ? stickyButton.dataset.addLabel : stickyButton.dataset.soldLabel;
        }
      });
    }
  });
}
if (!customElements.get('v3-catalog-filter')) {
  customElements.define('v3-catalog-filter', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const controls = this.querySelector('.v3-catalog-controls');
      if (!controls) return;
      const cards = [...this.querySelectorAll('[data-catalog-kind]')];
      const status = this.querySelector('[role="status"]');
      const buttons = [...controls.querySelectorAll('[data-filter]')];
      controls.hidden = false;
      buttons.forEach(button => button.addEventListener('click', () => {
        const filter = button.dataset.filter;
        buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        cards.forEach(card => { card.hidden = filter !== 'all' && card.dataset.catalogKind !== filter; });
        status.textContent = `${cards.filter(card => !card.hidden).length} ${status.dataset.countLabel}`;
      }));
    }
  });
}
if (!customElements.get('v3-carousel')) {
  customElements.define('v3-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const track = this.querySelector('.v3-carousel__track');
      const slides = [...track.children];
      const previous = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      const position = this.querySelector('[data-position]');
      if (slides.length < 2) return;
      this.querySelector('.v3-carousel__controls').hidden = false;
      let index = 0;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const step = slides[1].offsetLeft - slides[0].offsetLeft;
        index = max > 0 && track.scrollLeft >= max - 3 ? slides.length - 1 : Math.round(track.scrollLeft / step);
        previous.disabled = track.scrollLeft < 3;
        next.disabled = track.scrollLeft >= max - 3;
        if (position) position.textContent = `${index + 1} / ${slides.length}`;
      };
      const move = direction => {
        const target = Math.max(0, Math.min(slides.length - 1, index + direction));
        track.scrollTo({left: slides[target].offsetLeft - slides[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      });
      track.addEventListener('scroll', update, {passive:true});
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(track);
      update();
    }
    disconnectedCallback() { this.resizeObserver?.disconnect(); this.ready = false; }
  });
}
if (!customElements.get('v3-hero-video')) {
  customElements.define('v3-hero-video', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const video = this.querySelector('video');
      const button = this.querySelector('button');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let userPaused = false;
      let visible = false;
      const play = () => {
        if (!video.getAttribute('src')) {
          video.src = matchMedia('(max-width: 749px)').matches && this.dataset.sourceMobile
            ? this.dataset.sourceMobile : this.dataset.source;
        }
        video.muted = true;
        video.play().catch(() => { button.textContent = button.dataset.play; });
      };
      const sync = () => { button.textContent = video.paused ? button.dataset.play : button.dataset.pause; };
      video.addEventListener('playing', () => { this.classList.add('is-playing','has-played'); sync(); });
      video.addEventListener('pause', () => { this.classList.remove('is-playing'); sync(); });
      video.addEventListener('error', () => { this.classList.remove('is-playing','has-played'); button.hidden = true; });
      button.hidden = false;
      button.addEventListener('click', () => {
        if (video.paused) { userPaused = false; play(); }
        else { userPaused = true; video.pause(); }
      });
      const autoPlay = () => visible && !userPaused && !reduced.matches && !navigator.connection?.saveData && !document.hidden;
      this.observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        if (autoPlay()) play(); else video.pause();
      }, {threshold:0.15});
      this.observer.observe(this);
      this.onVisibility = () => { if (autoPlay()) play(); else video.pause(); };
      this.onMotion = () => { if (reduced.matches) video.pause(); };
      this.motion = reduced;
      reduced.addEventListener('change', this.onMotion);
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    disconnectedCallback() {
      this.querySelector('video')?.pause(); this.observer?.disconnect();
      this.motion?.removeEventListener('change', this.onMotion);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.ready = false;
    }
  });
}

if (!customElements.get('v3-ritual-tabs')) {
  customElements.define('v3-ritual-tabs', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      const panels = [...this.querySelectorAll('[role="tabpanel"]')];
      const select = (index, focus = false) => {
        tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          panels[i].hidden = i !== index;
        });
        if (focus) tabs[index].focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(i));
        tab.addEventListener('keydown', event => {
          const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          select(next, true);
        });
      });
      select(0);
      this.querySelector('[role="tablist"]').hidden = false;
    }
  });
}

// Ingredient shortcuts open the matching disclosure before native anchor navigation.
document.addEventListener('click', event => {
  const link = event.target.closest('a[href^="#v3-inci-"]');
  if (!link) return;
  const details = document.getElementById(link.getAttribute('href').slice(1));
  if (details instanceof HTMLDetailsElement) details.open = true;
});

// Keep Judge.me's own filtering and pagination available after the short preview.
if (!customElements.get('v3-review-preview')) {
  customElements.define('v3-review-preview', class extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      const content = this.querySelector('[data-review-content]');
      const button = this.querySelector('[data-review-toggle]');
      if (!content || !button) return;
      const expand = (expanded) => {
        this.toggleAttribute('data-expanded', expanded);
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? button.dataset.less : button.dataset.more;
      };
      const sync = () => {
        const reviews = content.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev');
        const hasMore = reviews.length > 2 || !!content.querySelector('.jdgm-paginate__next-page');
        this.toggleAttribute('data-preview-ready', hasMore);
        button.hidden = !hasMore;
      };
      this.onToggle = () => expand(!this.hasAttribute('data-expanded'));
      this.onFilter = (event) => {
        if (event.target.closest('.jdgm-histogram,.jdgm-sort-dropdown')) expand(true);
      };
      button.addEventListener('click', this.onToggle);
      content.addEventListener('click', this.onFilter);
      content.addEventListener('change', this.onFilter);
      this.observer = new MutationObserver(sync);
      this.observer.observe(content, { childList: true, subtree: true });
      sync();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.querySelector('[data-review-toggle]')?.removeEventListener('click', this.onToggle);
      const content = this.querySelector('[data-review-content]');
      content?.removeEventListener('click', this.onFilter);
      content?.removeEventListener('change', this.onFilter);
    }
  });
}
